import { ThemeModeContext } from '@/context/ThemeContext';
import { EdgeTheme, FlowTheme, ThemeSettings } from '@/types/theme';
import { useMediaQuery } from '@mui/material';
import { useEffect } from 'react';
import { useLocalStorage } from 'usehooks-ts';

export const ThemeModeProvider = ({ children }: { children: React.ReactNode }) => {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [settings, setSettings] = useLocalStorage<ThemeSettings>('theme.settings', {
    gridRatio: 15,
    nodePadding: 15,
    flowTheme: 'normal',
    edgeTheme: 'smart',
    mode: (prefersDarkMode && 'dark') || 'light'
  });

  const toggleMode = () => {
    setSettings({
      ...settings,
      mode: settings.mode === 'dark' ? 'light' : 'dark'
    });
  };

  const toggleFlowTheme = (flowTheme: FlowTheme) => {
    setSettings({
      ...settings,
      flowTheme: flowTheme
    });
  };

  const toggleEdgeTheme = (edgeTheme: EdgeTheme) => {
    setSettings({
      ...settings,
      edgeTheme: edgeTheme
    });
  };

  const updateGridRatio = (ratio: number) => {
    setSettings({
      ...settings,
      gridRatio: ratio
    });
  };

  const updateNodePadding = (padding: number) => {
    setSettings({
      ...settings,
      nodePadding: padding
    });
  };

  useEffect(() => {
    if (!settings.gridRatio || !settings.mode || !settings.edgeTheme || !settings.flowTheme) {
      setSettings({
        gridRatio: settings.gridRatio || 10,
        nodePadding: settings.nodePadding || 10,
        mode: settings.mode || 'dark',
        flowTheme: settings.flowTheme || 'normal',
        edgeTheme: settings.edgeTheme || 'smart'
      });
    }
  }, []);

  return (
    <ThemeModeContext.Provider
      value={{
        gridRatio: settings.gridRatio,
        setGridRatio: updateGridRatio,
        nodePadding: settings.nodePadding,
        setNodePadding: updateNodePadding,
        mode: settings.mode,
        toggleMode: toggleMode,
        flowTheme: settings.flowTheme,
        toggleFlowTheme: toggleFlowTheme,
        edgeTheme: settings.edgeTheme,
        toggleEdgeTheme: toggleEdgeTheme
      }}
    >
      {children}
    </ThemeModeContext.Provider>
  );
};
